import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, User, Shield, PenLine, Menu, X, Download, UserPlus, Megaphone } from 'lucide-react';
import WeatherWidget from './WeatherWidget';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO = '/logo.png';

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const out = async () => { await logout(); nav('/'); };

  return (
    <header data-testid="main-header" className="sticky top-0 z-50 bg-[#0B1F40] border-b-4 border-[#E50B15]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <img src={LOGO} alt="RP" className="h-8 w-8" />
          <div className="leading-none">
            <span className="text-base font-black text-white tracking-tight" style={{fontFamily:'Archivo Black'}}><span className="text-[#E50B15]">RP</span> NOTICIAS</span>
            <span className="block text-[8px] text-white/40 tracking-[0.2em] uppercase">La crítica de los hechos</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-0">
          {[
            {to:'/',label:'INICIO',tid:'nav-home'},
            {to:'/denuncia',label:'DENUNCIA',tid:'nav-denuncia',red:true,icon:<Megaphone className="h-3 w-3"/>},
            {to:'/descargar',label:'APP',tid:'nav-download',red:true,icon:<Download className="h-3 w-3"/>},
          ].map(l=>(
            <Link key={l.to} to={l.to} data-testid={l.tid} className={`px-4 py-1 text-[10px] font-bold tracking-[0.15em] uppercase border-r border-white/10 flex items-center gap-1 transition-colors ${l.red?'text-[#E50B15] hover:text-white':'text-white/60 hover:text-white'}`}>
              {l.icon}{l.label}
            </Link>
          ))}
          {user?.role==='admin'&&<Link to="/admin" data-testid="nav-admin" className="px-4 py-1 text-[10px] font-bold tracking-[0.15em] uppercase border-r border-white/10 text-white/60 hover:text-white flex items-center gap-1"><Shield className="h-3 w-3"/>ADMIN</Link>}
          {user?.role==='admin'&&<Link to="/register" data-testid="nav-register" className="px-4 py-1 text-[10px] font-bold tracking-[0.15em] uppercase border-r border-white/10 text-[#E50B15] hover:text-white flex items-center gap-1"><UserPlus className="h-3 w-3"/>REGISTRAR</Link>}
          {user&&<Link to="/dashboard" data-testid="nav-dashboard" className="px-4 py-1 text-[10px] font-bold tracking-[0.15em] uppercase text-white/60 hover:text-white flex items-center gap-1"><PenLine className="h-3 w-3"/>MIS NOTICIAS</Link>}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <WeatherWidget />
          {user?(
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="bg-white/10 text-white text-[10px] font-bold tracking-wide h-8 px-3 flex items-center gap-1.5 hover:bg-white/20 transition-colors brutal-shadow" style={{boxShadow:'2px 2px 0px #E50B15'}}>
                  <User className="h-3 w-3"/>{user.name}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-2 border-[#0B1F40] bg-white brutal-shadow">
                <DropdownMenuItem data-testid="menu-profile" onClick={()=>nav('/profile')} className="cursor-pointer text-xs font-bold"><User className="h-3 w-3 mr-2"/>Mi Perfil</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-dashboard" onClick={()=>nav('/dashboard')} className="cursor-pointer text-xs font-bold"><PenLine className="h-3 w-3 mr-2"/>Mis Noticias</DropdownMenuItem>
                {user.role==='admin'&&<DropdownMenuItem data-testid="menu-admin" onClick={()=>nav('/admin')} className="cursor-pointer text-xs font-bold"><Shield className="h-3 w-3 mr-2"/>Admin</DropdownMenuItem>}
                <DropdownMenuSeparator/>
                <DropdownMenuItem data-testid="menu-logout" onClick={out} className="cursor-pointer text-xs font-bold text-[#E50B15]"><LogOut className="h-3 w-3 mr-2"/>Salir</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ):(
            <div className="flex items-center gap-1">
              <Link to="/login" data-testid="nav-login"><button className="border-2 border-white/30 text-white text-[10px] font-bold h-8 px-4 hover:border-white transition-colors">ENTRAR</button></Link>
            </div>
          )}
        </div>

        <button data-testid="mobile-menu-toggle" className="md:hidden text-white p-1" onClick={()=>setOpen(!open)}>
          {open?<X className="h-5 w-5"/>:<Menu className="h-5 w-5"/>}
        </button>
      </div>

      {open&&(
        <div className="md:hidden bg-[#0B1F40] border-t border-white/10 p-4 space-y-2 animate-fade-in">
          <Link to="/" onClick={()=>setOpen(false)} className="block text-sm font-bold text-white/70 py-1">Inicio</Link>
          <Link to="/denuncia" onClick={()=>setOpen(false)} className="block text-sm font-bold text-[#E50B15] py-1">Denuncia Anónima</Link>
          <Link to="/descargar" onClick={()=>setOpen(false)} className="block text-sm font-bold text-[#E50B15] py-1">Descargar App</Link>
          {user&&<Link to="/profile" onClick={()=>setOpen(false)} className="block text-sm font-bold text-white/70 py-1">Mi Perfil</Link>}
          {user&&<Link to="/dashboard" onClick={()=>setOpen(false)} className="block text-sm font-bold text-white/70 py-1">Mis Noticias</Link>}
          {user?.role==='admin'&&<Link to="/admin" onClick={()=>setOpen(false)} className="block text-sm font-bold text-white/70 py-1">Admin</Link>}
          {user?.role==='admin'&&<Link to="/register" onClick={()=>setOpen(false)} className="block text-sm font-bold text-[#E50B15] py-1">Registrar Usuario</Link>}
          <div className="pt-2 border-t border-white/10 space-y-2">
            {user?<button onClick={out} className="w-full bg-[#E50B15] text-white text-sm font-bold py-2">Salir</button>:(
              <Link to="/login" onClick={()=>setOpen(false)} className="block"><button className="w-full border-2 border-white/30 text-white text-sm font-bold py-2">Entrar</button></Link>)}
          </div>
        </div>
      )}
    </header>
  );
}
